<template>
  <Teleport to="body">
    <Transition name="app-dialog-fade">
      <div
        v-if="dialogState.visible"
        class="app-dialog-root fixed inset-0 z-[120] flex items-center justify-center p-4"
        @click.self="handleBackdrop"
      >
        <div class="app-dialog-backdrop absolute inset-0 bg-slate-950/45 backdrop-blur-[2px]"></div>

        <Transition name="app-dialog-scale" appear>
          <div
            v-if="dialogState.visible"
            class="app-dialog-panel relative w-full max-w-[340px] overflow-hidden rounded-3xl border border-white/70 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.22)] dark:border-gray-700/80 dark:bg-gray-900"
            role="dialog"
            aria-modal="true"
            :aria-label="dialogState.title || dialogState.message"
          >
            <div class="px-6 pt-7 pb-2 text-center">
              <div
                class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
                :class="iconWrapClass"
              >
                <svg v-if="dialogState.type === 'success'" class="h-8 w-8" :class="iconClass" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M5 13l4 4L19 7" />
                </svg>
                <svg v-else-if="dialogState.type === 'warning' || dialogState.type === 'error'" class="h-8 w-8" :class="iconClass" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <svg v-else class="h-8 w-8" :class="iconClass" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                </svg>
              </div>
              <h2
                v-if="dialogState.title"
                class="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-50"
              >
                {{ dialogState.title }}
              </h2>
              <p
                class="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-300"
                :class="{ 'mt-0': !dialogState.title }"
              >
                {{ dialogState.message }}
              </p>
            </div>

            <div class="flex flex-col gap-2 px-5 pb-5 pt-4">
              <button
                type="button"
                class="app-dialog-btn app-dialog-btn--primary w-full rounded-2xl px-4 py-3.5 text-sm font-semibold text-white shadow-lg active:scale-[0.98] transition"
                @click="confirm"
              >
                {{ dialogState.confirmText }}
              </button>
              <button
                v-if="dialogState.showCancel"
                type="button"
                class="app-dialog-btn w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-600 transition active:scale-[0.98] dark:border-gray-700 dark:text-gray-300"
                @click="cancel"
              >
                {{ dialogState.cancelText }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { dialogState, resolveAppDialog } from '@/utils/appDialog'

const iconWrapClass = computed(() => {
  switch (dialogState.type) {
    case 'success':
      return 'bg-emerald-50 dark:bg-emerald-500/10'
    case 'warning':
      return 'bg-amber-50 dark:bg-amber-500/10'
    case 'error':
      return 'bg-rose-50 dark:bg-rose-500/10'
    default:
      return 'bg-brand/10'
  }
})

const iconClass = computed(() => {
  switch (dialogState.type) {
    case 'success':
      return 'text-emerald-500'
    case 'warning':
      return 'text-amber-500'
    case 'error':
      return 'text-rose-500'
    default:
      return 'text-brand'
  }
})

function confirm() {
  resolveAppDialog(true)
}

function cancel() {
  resolveAppDialog(false)
}

function handleBackdrop() {
  if (dialogState.closeOnBackdrop) {
    resolveAppDialog(false)
  }
}
</script>

<style scoped>
.app-dialog-btn--primary {
  background: linear-gradient(135deg, rgb(139 92 246), rgb(124 58 237));
  box-shadow: 0 12px 28px rgba(124, 58, 237, 0.28);
}

.app-dialog-fade-enter-active,
.app-dialog-fade-leave-active {
  transition: opacity 0.22s ease;
}

.app-dialog-fade-enter-from,
.app-dialog-fade-leave-to {
  opacity: 0;
}

.app-dialog-scale-enter-active {
  animation: app-dialog-in 0.28s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.app-dialog-scale-leave-active {
  animation: app-dialog-out 0.18s ease forwards;
}

@keyframes app-dialog-in {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes app-dialog-out {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
}
</style>
